/**
 * Internal Complaints Committee — one complaint.
 *
 * ⚠ This is the only screen in the CMS that displays the complaint text.
 * Opening it is recorded in the audit log by the API, because under the PoSH
 * Act 2013 (s.16) who *read* a complaint matters as much as who changed it.
 *
 * The attachment is fetched through the authenticated API route and handed to
 * the browser as a blob. It is never a plain link: the file lives in the
 * private R2 bucket and there is no anonymous URL that would resolve to it.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

import { api, BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { STATUS_META, statusBadge, type ComplaintStatus } from "./_app.icc-complaints.index";

export const Route = createFileRoute("/_app/icc-complaints/$id")({ component: ComplaintDetail });

interface Complaint {
  id: number;
  reference: string;
  name: string;
  department: string;
  reg_no: string;
  email: string;
  phone: string | null;
  complaint: string;
  attachment_name: string | null;
  attachment_mime: string | null;
  attachment_size: number | null;
  has_attachment: number;
  status: ComplaintStatus;
  notes: string | null;
  submitted_at: number;
  updated_at: number;
}

function fileSize(bytes: number | null) {
  if (!bytes) return "";
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function ComplaintDetail() {
  const { id } = Route.useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [notes, setNotes] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["icc-complaints", id],
    queryFn: () => api.get<Complaint>(`/admin/icc-complaints/${id}`),
  });

  // Seed the notes box once the record arrives, without clobbering edits in
  // progress on a background refetch.
  useEffect(() => {
    if (data && notes === "") setNotes(data.notes ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const save = useMutation({
    mutationFn: (patch: { status?: ComplaintStatus; notes?: string | null }) =>
      api.patch(`/admin/icc-complaints/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["icc-complaints"] });
      toast.success("Complaint updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/admin/icc-complaints/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["icc-complaints"] });
      toast.success("Complaint deleted");
      navigate({ to: "/icc-complaints" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * `credentials: "include"` because the session is a cross-site cookie, and a
   * blob because the response is only reachable with that cookie attached — an
   * <a href> would arrive unauthenticated and 401.
   */
  async function downloadAttachment() {
    if (!data) return;
    setDownloading(true);
    try {
      const r = await fetch(`${BASE}/admin/icc-complaints/${id}/attachment`, { credentials: "include" });
      if (!r.ok) throw new Error(r.status === 404 ? "Attachment not found" : "Download failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.attachment_name ?? `${data.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Complaint not found.</p>;

  const readOnly = !can("icc_complaints", "update");

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.reference}
        description={`Submitted ${format(new Date(data.submitted_at * 1000), "d MMMM yyyy 'at' HH:mm")}`}
        back={{ href: "/icc-complaints", label: "All complaints" }}
        actions={
          can("icc_complaints", "delete") ? (
            <Button variant="outline" className="text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(true)}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-lg border bg-amber-500/10 border-amber-500/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
        Confidential. Complaints to the Internal Committee are protected under the
        PoSH Act 2013 (s.16) — do not forward, print or discuss this outside the committee.
        Opening this page is recorded in the audit log.
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── complainant + complaint ── */}
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-lg border p-5">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Complainant</h3>
            <dl className="grid gap-4 sm:grid-cols-2">
              <div><dt className="text-xs text-muted-foreground">Name</dt><dd className="mt-0.5 font-medium">{data.name}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Department</dt><dd className="mt-0.5">{data.department}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Register no. / College ID</dt><dd className="mt-0.5 font-mono text-sm">{data.reg_no}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Email</dt><dd className="mt-0.5"><a className="text-primary hover:underline" href={`mailto:${data.email}`}>{data.email}</a></dd></div>
              {data.phone && (
                <div><dt className="text-xs text-muted-foreground">Phone</dt><dd className="mt-0.5"><a className="text-primary hover:underline" href={`tel:${data.phone.replace(/\s+/g, "")}`}>{data.phone}</a></dd></div>
              )}
            </dl>
          </section>

          <section className="rounded-lg border p-5">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Complaint</h3>
            {/* whitespace-pre-wrap, never dangerouslySetInnerHTML: this is
                untrusted text typed by a member of the public. */}
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{data.complaint}</p>
          </section>

          {data.has_attachment ? (
            <section className="rounded-lg border p-5">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Signed statement</h3>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{data.attachment_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {data.attachment_mime}{data.attachment_size ? ` · ${fileSize(data.attachment_size)}` : ""}
                  </p>
                </div>
                <Button variant="outline" onClick={downloadAttachment} disabled={downloading}>
                  <Download className="mr-2 h-4 w-4" />
                  {downloading ? "Preparing…" : "Download"}
                </Button>
              </div>
            </section>
          ) : null}
        </div>

        {/* ── committee actions ── */}
        <div className="space-y-6">
          <section className="rounded-lg border p-5">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Status</h3>
            <div className="mb-4">{statusBadge(data.status)}</div>
            <Select
              value={data.status}
              disabled={readOnly || save.isPending}
              onValueChange={(v) => save.mutate({ status: v as ComplaintStatus })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(STATUS_META) as ComplaintStatus[]).map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          <section className="rounded-lg border p-5">
            <Label htmlFor="notes" className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Committee notes
            </Label>
            <p className="mb-3 mt-1 text-xs text-muted-foreground">
              Internal only. Never shown to the complainant.
            </p>
            <Textarea
              id="notes"
              rows={8}
              value={notes}
              disabled={readOnly}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Actions taken, meeting dates, outcome…"
            />
            {!readOnly && (
              <Button
                className="mt-3 w-full"
                disabled={save.isPending || notes === (data.notes ?? "")}
                onClick={() => save.mutate({ notes: notes.trim() ? notes : null })}
              >
                {save.isPending ? "Saving…" : "Save notes"}
              </Button>
            )}
          </section>
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {data.reference}?</DialogTitle>
            <DialogDescription>
              The complaint and its signed statement are removed permanently. Committee
              records may need to be retained — check before deleting.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>
              {remove.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
